import type { PlanType } from '@/services/billing/plans';
import type { AugmentedAccount, AugmentedUser } from './types';

export const USER_DETAILS_COOKIE = '_current_user_details';

export interface UserDetailsInput {
  user: Pick<AugmentedUser, 'id'>;
  account: Pick<AugmentedAccount, 'id' | 'admin'>;
  planType: PlanType | undefined;
}

export function buildUserDetailsValue({ user, account, planType }: UserDetailsInput): string {
  // Segment order — including the literal `true` (a retired `new_ui` flag) — is a
  // contract with the docs parser. Preserve these five positions; docs no longer
  // consumes the former sixth segment of feature flags.
  const segments = [user.id, planType ?? '', account.id, true, account.admin];

  return segments.join('|');
}

// Reproduces js-cookie 2.2.1's write encoding used by Ember:
// decode characters such as `:` and `|` back to literals,
// leave a space/comma percent-encoded (so `cdp starter` → `cdp%20starter`).
export function encodeCookieValue(value: string): string {
  return encodeURIComponent(value).replace(
    /%(23|24|26|2B|3A|3C|3E|3D|2F|3F|40|5B|5D|5E|60|7B|7D|7C)/g,
    decodeURIComponent,
  );
}

// The shared parent domain so both the app and docs can read it; mirrors the
// Ember repo's config/environment.js.
export function getCookieDomain(hostname: string): string {
  if (hostname.endsWith('.customer.io')) {
    return '.customer.io';
  }
  if (hostname.endsWith('.devzilla.customerio.dev')) {
    return '.devzilla.customerio.dev';
  }
  return 'localhost';
}

function setUserDetailsCookie(value: string, extraAttributes: string[] = []): void {
  if (typeof document === 'undefined') {
    return;
  }

  const domain = getCookieDomain(window.location.hostname);

  // Attributes match Ember's `Cookies.set`: a session cookie (no expiry), Path=/
  // (js-cookie's default), SameSite=Strict, and Secure everywhere but local dev.
  // Clearing needs the same Domain and Path, or the browser keeps the cookie.
  const parts = [
    `${USER_DETAILS_COOKIE}=${value}`,
    `Domain=${domain}`,
    'Path=/',
    'SameSite=Strict',
    ...extraAttributes,
  ];
  if (domain !== 'localhost') {
    parts.push('Secure');
  }

  document.cookie = parts.join('; ');
}

export function writeUserDetailsCookie(input: UserDetailsInput): void {
  setUserDetailsCookie(encodeCookieValue(buildUserDetailsValue(input)));
}

export function clearUserDetailsCookie(): void {
  setUserDetailsCookie('', ['Max-Age=0']);
}
